import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { isPartner } from "@/lib/auth-utils";
import { ApplicationsStatuses } from "./utils";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/features/auth/context/AuthContext";

export const GetApplicationPlaceholderStringPath = (
  status: ApplicationsStatuses
) => {
  switch (status) {
    case "screening":
      return "status_labels.application_status.invited_to_interview";
    case "invited_to_interview":
      return "status_labels.application_status.invited_to_interview";
    case "hired":
      return "status_labels.application_status.hired";
    case "declined":
      return "status_labels.application_status.declined";
  }
  return "status_labels.application_status.invited_to_interview";
};

const GetInterviewStatusUpdateOptions = ({
  status
}: {
  status: ApplicationsStatuses;
}) => {
  const { t } = useTranslation(["applications"]);
  switch (status) {
    case "screening":
      return (
        <SelectContent>
          <SelectItem value="screening">
            {t(
              "application_management.status_section.status_options.screening"
            )}
          </SelectItem>
          <SelectItem value="invited_to_interview">
            {t(
              "application_management.status_section.status_options.invite_to_interview"
            )}
          </SelectItem>
          <SelectItem value="declined">
            {t("application_management.status_section.status_options.decline")}
          </SelectItem>
        </SelectContent>
      );
    case "invited_to_interview":
      return (
        <SelectContent>
          <SelectItem value="invited_to_interview">
            {t("status_labels.application_status.invited_to_interview")}
          </SelectItem>
          <SelectItem value="passed_interview">
            {t(
              "application_management.status_section.status_options.passed_interview"
            )}
          </SelectItem>
          <SelectItem value="failed_interview">
            {t(
              "application_management.status_section.status_options.failed_interview"
            )}
          </SelectItem>
          <SelectItem value="declined">
            {t("application_management.status_section.status_options.decline")}
          </SelectItem>
        </SelectContent>
      );
    case "passed_interview":
      return (
        <SelectContent>
          <SelectItem value="passed_interview">
            {t("status_labels.application_status.passed_interview")}
          </SelectItem>
          <SelectItem value="hired">
            {t("application_management.status_section.status_options.hire")}
          </SelectItem>
          <SelectItem value="declined">
            {t("application_management.status_section.status_options.decline")}
          </SelectItem>
        </SelectContent>
      );
    case "failed_interview":
      return (
        <SelectContent>
          <SelectItem value="failed_interview">
            {t("status_labels.application_status.failed_interview")}
          </SelectItem>
        </SelectContent>
      );
    case "hired":
      return (
        <SelectContent>
          <SelectItem value="hired">
            {t("status_labels.application_status.hired")}
          </SelectItem>
        </SelectContent>
      );
    case "declined":
      return (
        <SelectContent>
          <SelectItem value="declined">
            {t("status_labels.application_status.declined")}
          </SelectItem>
        </SelectContent>
      );
  }
};

interface ApplicationStatusManagementProps {
  application: any;
  statusUpdating: boolean;
  handleStatusChange: (status: ApplicationsStatuses) => void;
}

export function ApplicationStatusManagement({
  application,
  statusUpdating,
  handleStatusChange
}: ApplicationStatusManagementProps) {
  const { t } = useTranslation(["applications"]);
  const { profile } = useAuth();

  if (profile.role === "talent") {
    return null;
  }

  return (
    <div className="pt-6">
      <h3 className="text-lg font-semibold mb-4">
        {t("application_management.status_section.title")}
      </h3>
      <div className="w-full">
        <Select
          value={application.status}
          onValueChange={handleStatusChange}
          disabled={
            statusUpdating ||
            application.status === "hired" ||
            application.status === "declined" ||
            application.status === "failed_interview" ||
            isPartner(profile.role)
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue
              placeholder={t(
                GetApplicationPlaceholderStringPath(application.status)
              )}
            />
          </SelectTrigger>
          <GetInterviewStatusUpdateOptions status={application.status} />
        </Select>
        {statusUpdating && (
          <p className="text-sm text-gray-500 mt-2">{t("messages.updating")}</p>
        )}
      </div>
    </div>
  );
}
